/**
 * The destination strip across the top of the composer.
 *
 * It sits above everything else because it is the one choice the rest of the
 * screen depends on: the character counters, the preview tabs and the platform
 * settings pane all render from this selection, and none of them mean anything
 * until it is made.
 *
 * One cell per connected *account*, not per platform — a tenant can hold several
 * pages on the same network, and "publish to Facebook" is ambiguous when two of
 * them are connected.
 */

import { Check } from 'lucide-react';

import { cn } from '../../../lib/utils';
import { useTranslation } from '../../../context/LanguageContext';
import { PLATFORM_CONFIGS } from '../../../config/platforms';
import type { ConnectedAccount } from '../../../types/composer';
import { PlatformIcon } from './PlatformIcon';

interface PlatformSelectorProps {
  accounts: ConnectedAccount[];
  loading: boolean;
  selectedIds: string[];
  onToggle: (id: string) => void;
}

export function PlatformSelector({
  accounts,
  loading,
  selectedIds,
  onToggle,
}: Readonly<PlatformSelectorProps>) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="flex gap-2 overflow-hidden rounded-xl border border-slate-200 bg-white p-2">
        {Array.from({ length: 6 }, (_, i) => (
          <div key={i} className="h-9 w-32 shrink-0 animate-pulse rounded-lg bg-slate-100" />
        ))}
      </div>
    );
  }

  if (accounts.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-3 text-center">
        <p className="text-[12.5px] font-semibold text-slate-700">{t('composer.noAccounts')}</p>
        <p className="mt-0.5 text-[11px] text-slate-500">{t('composer.noAccountsHint')}</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white">
      <div className="flex flex-wrap items-stretch">
        {accounts.map((account, index) => {
          const cfg = PLATFORM_CONFIGS[account.platform];
          const selected = selectedIds.includes(account.id);

          return (
            <label
              key={account.id}
              className={cn(
                'flex flex-1 cursor-pointer items-center gap-2 px-3 py-2.5 transition-colors focus-within:ring-2 focus-within:ring-inset focus-within:ring-blue-500',
                // Cells butt against each other as one strip, so the separator
                // is a left border on everything but the first.
                index > 0 && 'border-l border-slate-200',
                selected ? 'bg-blue-50/70' : 'hover:bg-slate-50'
              )}
            >
              <input
                type="checkbox"
                checked={selected}
                onChange={() => onToggle(account.id)}
                className="sr-only"
              />
              <span
                aria-hidden
                className={cn(
                  'flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors',
                  selected ? 'border-blue-600 bg-blue-600' : 'border-slate-300 bg-white'
                )}
              >
                {selected && <Check className="h-3 w-3 text-white" strokeWidth={3.5} />}
              </span>

              <PlatformIcon platform={account.platform} size={15} brandColor />

              <span className="min-w-0 flex-1">
                <span
                  className={cn(
                    'block truncate text-[12.5px] font-semibold leading-tight',
                    selected ? 'text-slate-900' : 'text-slate-600'
                  )}
                >
                  {cfg.name}
                </span>
                <span className="block truncate text-[10.5px] leading-tight text-slate-400">
                  {account.name}
                </span>
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
}
